import { User } from '@features/auth/auth-client-adapter'
import { AppType } from '@features/app-type'
import { Organization } from '@st/sdk'
import { Effect } from 'effect'
import { AnalyticsEvent } from './analytics-events'
import { AnalyticsClient, AnalyticsProvider, AuthContext, EventData, TrackOptions } from './core'
import { GoogleTagManagerService } from './providers/google-tag-manager'
import { IntercomAnalyticsService } from './providers/intercom'
import { SentryAnalyticsService } from './providers/sentry'

export function makeAnalyticsClient(appType: AppType): AnalyticsClient {
  const providers = getAnalyticsProviders(appType)

  const runProviders = (
    operation: (provider: AnalyticsProvider) => Effect.Effect<void> | undefined
  ) =>
    Effect.forEach(providers, (provider) => operation(provider) ?? Effect.void, {
      discard: true
    })

  return {
    track(event: AnalyticsEvent, data: EventData = {}, options: TrackOptions = {}) {
      return runProviders((provider) => provider.track?.(event, data, options))
    },
    trackLocationChanged(location: string) {
      return runProviders((provider) => provider.trackLocationChanged?.(location))
    },
    trackUserIdentified(user: User, authContext: AuthContext) {
      return runProviders((provider) => provider.trackUserIdentified?.(user, authContext))
    },
    trackUserAnonymous() {
      return runProviders((provider) => provider.trackUserAnonymous?.())
    },
    setOrganization(organization: Organization) {
      return runProviders((provider) => provider.setOrganization?.(organization))
    },
    setGroup(groupName, groupId, properties) {
      return runProviders((provider) => provider.setGroup?.(groupName, groupId, properties))
    }
  }
}

function getAnalyticsProviders(appType: AppType): AnalyticsProvider[] {
  switch (appType) {
    case 'organization':
      return [SentryAnalyticsService, GoogleTagManagerService, IntercomAnalyticsService]
    case 'client':
      return [SentryAnalyticsService]
    default:
      return []
  }
}
